/** PROBATION-REVIEW-001 P3 — Reporting Manager review drawer (ratings, feedback, outcome). */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../ui/sheet';
import { Segmented, fmtDate, REVIEW_STATUS, StatusPill } from './probationUi';
import { API, h, err, addMonths, MIN_TEXT, MIN_REASON, RATINGS } from './probationApi';

const empty = () => ({ ratings: {}, strengths: '', improvement_areas: '', comments: '', outcome: 'confirmed', reason: '', revised_end_date: '' });

export default function ReviewDrawer({ open, onOpenChange, review, employee, probation, onSubmitted }) {
  const [f, setF] = useState(empty());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const r = review?.recommendation;
    setF(r ? { ratings: { ...r.ratings }, strengths: r.strengths, improvement_areas: r.improvement_areas, comments: r.comments, outcome: r.outcome,
               reason: r.extension?.reason || '', revised_end_date: r.extension?.revised_end_date || '' } : empty());
  }, [open, review]);
  if (!review) return null;
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const end = probation?.end_date || review.due_date;
  const floor = end > new Date().toISOString().slice(0, 10) ? end : new Date().toISOString().slice(0, 10);
  const textsOk = ['strengths', 'improvement_areas', 'comments'].every(k => f[k].trim().length >= MIN_TEXT);
  const ratingsOk = RATINGS.every(r => f.ratings[r.key] >= 1);
  const extOk = f.outcome !== 'extended' || (f.reason.trim().length >= MIN_REASON && f.revised_end_date > floor);
  const ready = textsOk && ratingsOk && extOk && !busy;
  const returned = review.status === 'returned' && review.hr_decision?.note;
  const atLimit = f.outcome === 'extended' && probation && (probation.extensions_count || 0) >= (probation.max_extensions || 2);

  const submit = async () => {
    const body = { outcome: f.outcome, ratings: f.ratings, strengths: f.strengths.trim(), improvement_areas: f.improvement_areas.trim(), comments: f.comments.trim(),
                   extension: f.outcome === 'extended' ? { reason: f.reason.trim(), revised_end_date: f.revised_end_date } : null };
    try { setBusy(true); await axios.post(`${API}/api/hr/probation/reviews/${review.id}/submit`, body, { headers: h() });
      toast.success(review.status === 'returned' ? 'Revised review submitted' : 'Review submitted to HR'); onOpenChange(false); onSubmitted?.(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] p-0 flex flex-col" data-testid="review-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-[16px] font-semibold text-slate-900">Probation review · {employee?.full_name || review.employee_name}</SheetTitle>
              <SheetDescription className="text-[12.5px] text-slate-500">Cycle {review.cycle_no} · due {fmtDate(review.due_date)} · {employee?.team || review.team_at_schedule || '—'}{employee?.date_of_joining ? ` · joined ${fmtDate(employee.date_of_joining)}` : ''}</SheetDescription>
            </div>
            <StatusPill map={REVIEW_STATUS} status={review.runtime_status || review.status} testid="review-drawer-status" />
          </div>
          {returned && <p className="mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[12.5px] text-amber-800 prb-reveal" data-testid="review-return-note"><span className="font-semibold">Returned by HR:</span> {review.hr_decision.note}</p>}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <section data-testid="review-ratings">
            <p className="prb-metric-label mb-2">Performance ratings</p>
            <div className="space-y-1.5">
              {RATINGS.map(r => (
                <div key={r.key} className="prb-rating" data-testid={`rating-${r.key}`}>
                  <span className="text-[13px] font-medium text-slate-700">{r.label}</span>
                  <div className="prb-scale" role="radiogroup" aria-label={r.label}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} type="button" role="radio" aria-checked={f.ratings[r.key] === n} className={`prb-scale-btn ${f.ratings[r.key] === n ? 'on' : ''} ${f.ratings[r.key] >= n ? 'lit' : ''}`}
                        onClick={() => set('ratings', { ...f.ratings, [r.key]: n })} data-testid={`rating-${r.key}-${n}`}>{n}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3" data-testid="review-feedback">
            <p className="prb-metric-label">Written feedback <span className="normal-case tracking-normal text-slate-400">(min {MIN_TEXT} characters each)</span></p>
            {[['strengths', 'Strengths'], ['improvement_areas', 'Areas to improve'], ['comments', 'Overall comments']].map(([k, label]) => (
              <label key={k} className="block">
                <span className="flex items-center justify-between text-[12.5px] font-medium text-slate-600 mb-1">{label}<span className={`text-[11px] ${f[k].trim().length >= MIN_TEXT ? 'text-emerald-600' : 'text-slate-400'}`}>{f[k].trim().length}/{MIN_TEXT}</span></span>
                <textarea className="prb-input !h-auto min-h-[64px] py-2 resize-y" rows={2} value={f[k]} onChange={e => set(k, e.target.value)} data-testid={`review-${k}`} />
              </label>
            ))}
          </section>

          <section data-testid="review-outcome">
            <p className="prb-metric-label mb-2">Recommendation</p>
            <Segmented testid="review-outcome-seg" value={f.outcome} onChange={v => set('outcome', v)} options={[{ value: 'confirmed', label: 'Confirm probation' }, { value: 'extended', label: 'Extend probation' }]} />
            {f.outcome === 'extended' && (
              <div className="mt-3 space-y-3 prb-reveal" data-testid="review-extension">
                {atLimit && <p className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-[12.5px] text-rose-700" data-testid="review-extension-limit">Maximum extensions reached ({probation.max_extensions}). An HR Manager override is required before this can be submitted.</p>}
                <label className="block"><span className="text-[12.5px] font-medium text-slate-600 mb-1 block">Reason for extension <span className="text-slate-400">(min {MIN_REASON})</span></span>
                  <input className="prb-input" value={f.reason} onChange={e => set('reason', e.target.value)} data-testid="review-extension-reason" /></label>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="block"><span className="text-[12.5px] font-medium text-slate-600 mb-1 block">Revised end date</span>
                    <input type="date" className="prb-input w-[170px]" min={floor} value={f.revised_end_date} onChange={e => set('revised_end_date', e.target.value)} data-testid="review-extension-date" /></label>
                  <div className="flex gap-1.5">{[1, 2, 3].map(m => <button key={m} type="button" className="prb-chip !h-8" onClick={() => set('revised_end_date', addMonths(end, m))} data-testid={`review-extension-quick-${m}`}>+{m} mo</button>)}</div>
                  {f.revised_end_date && <span className="text-[12px] text-slate-500 self-center">Current end {fmtDate(end)} → {fmtDate(f.revised_end_date)}</span>}
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-3 bg-white">
          <p className="text-[12px] text-slate-500">{ready ? 'Ready to submit to HR Manager for approval.' : 'Complete all ratings, feedback and recommendation details.'}</p>
          <div className="flex gap-2">
            <button type="button" className="prb-btn-ghost" onClick={() => onOpenChange(false)} data-testid="review-cancel-btn">Cancel</button>
            <button type="button" className="prb-btn-primary" disabled={!ready || atLimit} onClick={submit} data-testid="review-submit-btn">{review.status === 'returned' ? 'Resubmit review' : 'Submit review'}</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
